import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';
import QRScanner from '../components/QRScanner';
import NoImage from '../components/NoImage';
import locationsData from '../data/locations.json';
import muzeData from '../data/muze-db.json';
import {
  MapPin,
  ArrowRight,
  Search,
  Filter,
  Loader
} from 'lucide-react';
import { getStoredLanguage, normalizeLanguage } from '../utils/language';

const HomePage = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState(getStoredLanguage());

  const translateCategory = (category) => {
    const mapEn = {
      'Müzeler': 'Museums',
      'Anıtlar': 'Monuments',
      'Tarihi Mekanlar': 'Historic Sites',
      'Müze': 'Museum',
      'Kültür': 'Culture',
      'Tarih': 'History',
      'Sanat': 'Art',
      'Mimari': 'Architecture',
      'Doğa': 'Nature'
    };
    const mapRu = {
      'Müzeler': 'Музеи',
      'Anıtlar': 'Памятники',
      'Tarihi Mekanlar': 'Исторические места',
      'Müze': 'Музей',
      'Kültür': 'Культура',
      'Tarih': 'История',
      'Sanat': 'Искусство',
      'Mimari': 'Архитектура',
      'Doğa': 'Природа'
    };
    if (currentLanguage === 'EN') return mapEn[category] || category;
    if (currentLanguage === 'RU') return mapRu[category] || mapEn[category] || category;
    return category;
  };

  const t = (tr, en, ru) => {
    if (currentLanguage === 'EN') return en;
    if (currentLanguage === 'RU') return ru || en || tr;
    return tr;
  };

  const getTitle = (location) => {
    if (currentLanguage === 'EN') return location.titleEng || location.title;
    if (currentLanguage === 'RU') return location.titleRu || location.titleEng || location.title;
    return location.title;
  };

  const getDescription = (location) => {
    if (currentLanguage === 'EN') return location.descriptionEng || location.description;
    if (currentLanguage === 'RU') return location.descriptionRu || location.descriptionEng || location.description;
    return location.description;
  };

  const getCategory = (location) => {
    if (currentLanguage === 'EN') return location.categoryEng || location.category;
    if (currentLanguage === 'RU') return location.categoryRu || location.categoryEng || location.category;
    return location.category;
  };

  // Load data on component mount
  useEffect(() => {
    // initialize language
    try {
      setCurrentLanguage(getStoredLanguage());
    } catch (_) {}
    const handler = (e) => {
      const next = normalizeLanguage(e?.detail?.language);
      setCurrentLanguage(next);
    };
    if (typeof window !== 'undefined') window.addEventListener('app-language-change', handler);
    return () => { if (typeof window !== 'undefined') window.removeEventListener('app-language-change', handler); };
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Simulate loading delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Start from base locations to keep general info (image, coords, etc.)
        // Category translation map
        const categoryMapTrToEn = {
          'Müzeler': 'Museums',
          'Anıtlar': 'Monuments',
          'Tarihi Mekanlar': 'Historic Sites',
          'Müze': 'Museum',
          'Kültür': 'Culture',
          'Tarih': 'History',
          'Sanat': 'Art',
          'Mimari': 'Architecture',
          'Doğa': 'Nature'
        };
        const categoryMapTrToRu = {
          'Müzeler': 'Музеи',
          'Anıtlar': 'Памятники',
          'Tarihi Mekanlar': 'Исторические места',
          'Müze': 'Музей',
          'Kültür': 'Культура',
          'Tarih': 'История',
          'Sanat': 'Искусство',
          'Mimari': 'Архитектура',
          'Doğa': 'Природа'
        };

        const museIntroEn = {
          1: "City Museum - A museum showcasing Samsun's history and cultural heritage.",
          2: 'Onur Monument - A significant monument symbolizing Samsun and the War of Independence.',
          3: 'Tobacco Pier - The historic pier where Atatürk arrived in Samsun.',
          4: 'Alaçam Exchange Museum - A museum about the population exchange and migration stories.',
          5: 'Amazon Village - A thematic site inspired by the legendary Amazons.',
          6: 'Samsun Museum - A modern museum presenting the cultural heritage of Samsun.',
          7: 'Sadi Lodge - A historical lodge reflecting Samsun’s spiritual heritage.',
          8: 'İlkadım Monument - Monument commemorating the first step of independence in Samsun.',
          9: 'Gazi Museum - Museum where Atatürk stayed during his visit to Samsun.',
          10: 'Havza Atatürk House - The house where Atatürk stayed in Havza.',
          11: 'Bandırma Ferry - The ferry that brought Atatürk to Samsun.',
          12: 'Bafra Tobacco Museum - A museum dedicated to tobacco culture in Bafra.'
        };
        const museIntroRu = {
          1: 'Городской музей — экспозиция истории и культурного наследия Самсуна.',
          2: 'Памятник Чести — символ Самсуна и Войны за независимость.',
          3: 'Табачный пирс — историческое место прибытия Ататюрка в Самсун.',
          4: 'Музей обмена Алачам — истории переселений и миграций.',
          5: 'Деревня Амазонок — тематическая зона, вдохновленная легендарными амазонками.',
          6: 'Музей Самсуна — современный музей культурного наследия города.',
          7: 'Обитель Сади — исторический комплекс, отражающий духовное наследие Самсуна.',
          8: 'Памятник İlkadım — символ первого шага к независимости в Самсуне.',
          9: 'Музей Гази — место, где останавливался Ататюрк во время визита в Самсун.',
          10: 'Дом Ататюрка в Хавзе — дом, где жил Ататюрк в Хавзе.',
          11: 'Паром Бандирма — корабль, доставивший Ататюрка в Самсун.',
          12: 'Музей табака Бафры — посвящён табачной культуре Бафры.'
        };

        const locationsList = locationsData.locations.map((baseLoc) => {
          // If this is a museum (exists in muzeCode mapping), build sections from muze-db.json
          const muzeCodeMapping = {
            1: 'km',
            2: 'oa',
            3: 'ti',
            4: 'ac',
            5: 'az',
            6: 'sm',
            7: 'st',
            8: 'ia',
            9: 'gz',
            10: 'hv',
            11: 'bv',
            12: 'bf'
          };
          const muzeCode = muzeCodeMapping[baseLoc.id];
          if (!muzeCode) return baseLoc;

          const allSections = muzeData.sectionList.filter(s => s.muzeadi === muzeCode);
          let sections;
          if (muzeCode === 'km') {
            const main = allSections.filter(s => s.no.length === 3);
            const subs = allSections.filter(s => s.no.length === 4);
            sections = main.map(ms => ({
              id: `section-${ms.muzeadi}-${ms.no}`,
              title: ms.name,
              titleEng: ms.name_eng,
              description: ms.text?.substring(0, 200) + '...' || 'Açıklama mevcut değil',
              descriptionEng: ms.text_eng ? (ms.text_eng.substring(0, 200) + '...') : undefined,
              qrCode: `${ms.muzeadi.toUpperCase()}_${ms.no}`,
              media: { audio: ms.src, images: ms.image ? [ms.image] : [] },
              content: { text: ms.text, textEng: ms.text_eng },
              subsections: subs
                .filter(ss => ss.no.startsWith(ms.no))
                .map(ss => ({
                  id: `subsection-${ss.muzeadi}-${ss.no}`,
                  title: ss.name,
                  titleEng: ss.name_eng,
                  description: ss.text?.substring(0, 200) + '...' || 'Açıklama mevcut değil',
                  descriptionEng: ss.text_eng ? (ss.text_eng.substring(0, 200) + '...') : undefined,
                  qrCode: `${ss.muzeadi.toUpperCase()}_${ss.no}`,
                  media: { audio: ss.src, images: ss.image ? [ss.image] : [] },
                  content: { text: ss.text, textEng: ss.text_eng }
                }))
            }));
          } else {
            sections = allSections.map(s => ({
              id: `section-${s.muzeadi}-${s.no}`,
              title: s.name,
              titleEng: s.name_eng,
              description: s.text?.substring(0, 200) + '...' || 'Açıklama mevcut değil',
              descriptionEng: s.text_eng ? (s.text_eng.substring(0, 200) + '...') : undefined,
              qrCode: `${s.muzeadi.toUpperCase()}_${s.no}`,
              media: { audio: s.src, images: s.image ? [s.image] : [] },
              content: { text: s.text, textEng: s.text_eng },
              subsections: []
            }));
          }

          // Add English and Russian names for museums on home grid
          const englishNameMap = {
            1: 'City Museum',
            2: 'Onur Monument',
            3: 'Tobacco Pier',
            4: 'Alaçam Exchange Museum',
            5: 'Amazon Village',
            6: 'Samsun Museum',
            7: 'Sadi Lodge',
            8: 'İlkadım Monument',
            9: 'Gazi Museum',
            10: 'Havza Atatürk House',
            11: 'Bandırma Ferry',
            12: 'Bafra Tobacco Museum'
          };
          const russianNameMap = {
            1: 'Городской музей',
            2: 'Памятник Чести',
            3: 'Табачный пирс',
            4: 'Музей обмена Алачам',
            5: 'Деревня Амазонок',
            6: 'Музей Самсуна',
            7: 'Обитель Сади',
            8: 'Памятник İlkadım',
            9: 'Музей Гази',
            10: 'Дом Ататюрка в Хавзе',
            11: 'Паром Бандирма',
            12: 'Музей табака Бафры'
          };
          const titleEng = englishNameMap[baseLoc.id] || baseLoc.title;
          const titleRu = russianNameMap[baseLoc.id] || titleEng || baseLoc.title;
          const descriptionEng = museIntroEn[baseLoc.id] || baseLoc.description;
          const descriptionRu = museIntroRu[baseLoc.id] || descriptionEng || baseLoc.description;
          const categoryEng = categoryMapTrToEn[baseLoc.category] || baseLoc.category;
          const categoryRu = categoryMapTrToRu[baseLoc.category] || categoryEng || baseLoc.category;
          return { ...baseLoc, sections, titleEng, titleRu, descriptionEng, descriptionRu, categoryEng, categoryRu };
        });
        setLocations(locationsList);
        // When language changes, update filtered to re-render
        setFilteredLocations(locationsList);
        
        // Extract unique categories
        const uniqueCategories = [...new Set(locationsList.map(loc => loc.category))];
        setCategories(uniqueCategories);
        
        setLoading(false);
      } catch (error) {
        // Handle error silently in production
        setLoading(false);
        // Could add user notification here
      }
    };

    loadData();
  }, [currentLanguage]);

  // Filter locations based on search and category
  useEffect(() => {
    let filtered = locations;

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(location => location.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(location =>
        getTitle(location).toLowerCase().includes(term) ||
        (getDescription(location) || '').toLowerCase().includes(term) ||
        getCategory(location).toLowerCase().includes(term)
      );
    }

    setFilteredLocations(filtered);
  }, [locations, selectedCategory, searchTerm]);

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
  };

  const handleLocationClick = (location) => {
    // Müze kodları mapping
    const muzeCodeMapping = {
      1: 'km', // Kent Müzesi
      2: 'oa', // Onur Anıtı
      3: 'ti', // Tütün İskelesi
      4: 'ac', // Alaçam Mübadele Müzesi
      5: 'az', // Amazon Köyü
      6: 'sm', // Samsun Müzesi
      7: 'st', // Sadi Tekkesi
      8: 'ia', // İlkadım Anıtı
      9: 'gz', // Gazi Müzesi
      10: 'hv', // Havza Atatürk Evi
      11: 'bv', // Bandırma Vapuru
      12: 'bf'  // Bafra Tütün Müzesi
    };

    // Müze kodunu bul
    const muzeCode = muzeCodeMapping[location.id];
    
    if (muzeCode) {
      // Müze ise müze URL'sine yönlendir
      navigate(`/muze/${muzeCode}/1`);
    } else {
      // Müze değilse normal location URL'sine yönlendir
      navigate(`/location/${location.id}`);
    }
  };

  const handleQRScan = () => {
    setIsQRScannerOpen(true);
  };

  const handleQRScanResult = (result) => {
    console.log('Ana sayfada QR kod tarandı:', result);

    // QR kod formatını kontrol et ve uygun URL'ye yönlendir
    if (result) {
      const baseUrl = 'https://samsunkesfet.com';
      
      // Tam link formatında QR kod kontrolü
      if (result.startsWith('https://')) {
        console.log('Tam link QR kodu tespit edildi:', result);
        // URL'den path'i çıkar ve yönlendir
        try {
          const url = new URL(result);
          const path = url.pathname;
          console.log('Yönlendirilecek path:', path);
          window.location.href = path;
          setIsQRScannerOpen(false);
          return;
        } catch (error) {
          console.error('URL parse hatası:', error);
        }
      }

      // Path formatında QR kod kontrolü (/muze/gz/1 gibi)
      if (result.startsWith('/muze/')) {
        console.log('Path formatında QR kodu tespit edildi:', result);
        window.location.href = result;
        setIsQRScannerOpen(false);
        return;
      }

      // Eski format QR kodları için uyumluluk (KM_101 gibi)
      const muzeCodeMatch = result.match(/^([A-Z]{1,2})_(\d+)$/);
      if (muzeCodeMatch) {
        const [, muzeCodeUpper, sectionNo] = muzeCodeMatch;
        const muzeCodeLower = muzeCodeUpper.toLowerCase();

        console.log('Ana sayfada müze kodu algılandı:', muzeCodeLower, sectionNo);

        // Direkt URL'ye yönlendir
        window.location.href = `/muze/${muzeCodeLower}/${sectionNo}`;
        setIsQRScannerOpen(false);
        return;
      }

      // Eski format QR kodları için location ara ve müze koduna göre yönlendir
      const location = locations.find(loc => {
        if (loc.sections) {
          for (const section of loc.sections) {
            if (section.qrCode === result) return true;
            if (section.subsections) {
              for (const subsection of section.subsections) {
                if (subsection.qrCode === result) return true;
              }
            }
          }
        }
        return false;
      });

      if (location) {
        // Müze kodları mapping
        const muzeCodeMapping = {
          1: 'km', // Kent Müzesi
          2: 'oa', // Onur Anıtı
          3: 'ti', // Tütün İskelesi
          4: 'ac', // Alaçam Mübadele Müzesi
          5: 'az', // Amazon Köyü
          6: 'sm', // Samsun Müzesi
          7: 'st', // Sadi Tekkesi
          8: 'ia', // İlkadım Anıtı
          9: 'gz', // Gazi Müzesi
          10: 'hv', // Havza Atatürk Evi
          11: 'bv', // Bandırma Vapuru
          12: 'bf'  // Bafra Tütün Müzesi
        };

        // Müze kodunu bul
        const muzeCode = muzeCodeMapping[location.id];
        
        if (muzeCode) {
          // QR kodundan bölüm numarasını çıkar
          const sectionMatch = result.match(/^[A-Z]{1,2}_(\d+)$/);
          if (sectionMatch) {
            const sectionNumber = sectionMatch[1];
            window.location.href = `/muze/${muzeCode}/${sectionNumber}`;
          } else {
            // Bölüm numarası bulunamazsa ilk bölüme git
            window.location.href = `/muze/${muzeCode}/1`;
          }
        } else {
          // Müze değilse ana sayfaya yönlendir
          window.location.href = '/';
        }
        setIsQRScannerOpen(false);
        return;
      }
    }

    alert('QR kod tanınmadı: ' + result);
    setIsQRScannerOpen(false);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const LocationCard = ({ location }) => {
    const [imageError, setImageError] = useState(false);

    return (
      <div
        onClick={() => handleLocationClick(location)}
        className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 cursor-pointer group touch-manipulation"
      >
        <div className="relative h-40 sm:h-48 overflow-hidden">
          {location.image && !imageError ? (
            <>
              <img
                src={location.image}
                alt={getTitle(location)}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                onError={() => setImageError(true)}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </>
          ) : (
            <NoImage
              className="w-full h-full"
              title={t('Görsel Yok', 'No Image', 'Изображение отсутствует')}
              description={getTitle(location)}
              showIcon={true}
              showText={false}
            />
          )}
          <div className="absolute top-3 right-3 sm:top-4 sm:right-4">
            <span className="bg-primary-500 text-white px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-medium">
              {getCategory(location)}
            </span>
          </div>
        </div>

      <div className="p-4 sm:p-6">
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
          {getTitle(location)}
        </h3>
        <p className="text-gray-600 text-sm mb-3 sm:mb-4 line-clamp-2">
          {getDescription(location)}
        </p>

        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center text-gray-500 text-xs sm:text-sm min-w-0 flex-1 mr-2">
            <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
            <span className="truncate">{location.address}</span>
          </div>

          <div className="flex items-center text-primary-600 font-medium group-hover:text-primary-700 flex-shrink-0">
            <span className="text-xs sm:text-sm mr-1">{t('Keşfet', 'Explore', 'Исследовать')}</span>
            <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </div>
    );
  };

  const LoadingCard = () => (
    <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden animate-pulse">
      <div className="h-40 sm:h-48 bg-gray-300"></div>
      <div className="p-4 sm:p-6">
        <div className="h-5 sm:h-6 bg-gray-300 rounded mb-2"></div>
        <div className="h-4 bg-gray-300 rounded mb-3 sm:mb-4"></div>
        <div className="h-3 sm:h-4 bg-gray-300 rounded w-3/4"></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Header
        onSearch={handleSearch}
        onQRScan={handleQRScan}
      />

      <div className="flex">
        {/* Sidebar */}
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          categories={categories}
          selectedCategory={selectedCategory}
          onCategorySelect={handleCategoryFilter}
        />

        {/* Main Content */}
        <main className="flex-1 bg-gray-50">
          <div className="px-4 py-4 sm:py-6">
            {/* Mobile Sidebar Toggle */}
            <div className="lg:hidden mb-4 grid grid-cols-2 gap-3">
              <button
                onClick={toggleSidebar}
                className="flex items-center justify-center px-3 py-2 sm:px-4 sm:py-2 bg-primary-500 text-white rounded-lg font-medium shadow hover:bg-primary-600 transition-colors touch-manipulation text-sm sm:text-base"
              >
                <Filter className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="mr-2">{t('Kategoriler', 'Categories', 'Категории')}</span>
                <span className="bg-primary-400 text-primary-100 px-2 py-0.5 rounded-full text-xs flex-shrink-0">
                  {categories.length}
                </span>
              </button>

              {/* Ulaşım ve İletişim Butonu */}
              <button
                onClick={() => navigate('/contact-info')}
                className="flex items-center justify-center px-3 py-2 sm:px-4 sm:py-2 bg-blue-500 text-white rounded-lg font-medium shadow hover:bg-blue-600 transition-colors touch-manipulation text-sm sm:text-base"
              >
                <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="whitespace-nowrap">{t('Ulaşım', 'Transport', 'Транспорт')}</span>
              </button>
            </div>

            {/* Results Header - Mobile Optimized */}
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="mb-2 sm:mb-0">
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                    {selectedCategory
                      ? translateCategory(selectedCategory)
                      : t('Samsun\'u Keşfedin', 'Discover Samsun', 'Откройте Самсун')}
                  </h1>
                  <p className="text-gray-600 mt-1 text-sm sm:text-base">
                    {loading ? (
                      <span className="flex items-center">
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        {t('Yükleniyor...', 'Loading...', 'Загрузка...')}
                      </span>
                    ) : (
                      <>
                        {filteredLocations.length} {t('konum', 'locations', 'мест')}
                        {searchTerm && (currentLanguage === 'EN' ? ` for "${searchTerm}"` : currentLanguage === 'RU' ? ` по запросу "${searchTerm}"` : ` "${searchTerm}" için`)}
                      </>
                    )}
                  </p>
                </div>

                {!loading && filteredLocations.length > 0 && (
                  <div className="hidden sm:flex items-center text-xs text-gray-500 mt-2 sm:mt-0">
                    <Search className="w-3 h-3 mr-1" />
                    <span>{t('Detaylar için tıklayın', 'Tap for details', 'Нажмите для деталей')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Location Grid - Mobile Optimized */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {loading ? (
                // Loading skeletons
                Array.from({ length: 6 }).map((_, index) => (
                  <LoadingCard key={index} />
                ))
              ) : filteredLocations.length > 0 ? (
                // Location cards
                filteredLocations.map((location) => (
                  <LocationCard key={location.id} location={location} />
                ))
              ) : (
                // No results
                <div className="col-span-full text-center py-12 sm:py-16">
                  <div className="max-w-md mx-auto px-4">
                    <Search className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                      {t('Sonuç bulunamadı', 'No results found', 'Ничего не найдено')}
                    </h3>
                    <p className="text-gray-600 mb-6 text-sm sm:text-base">
                      {t('Arama kriterlerinize uygun konum bulunamadı. Farklı anahtar kelimeler deneyin.', 'No locations match your search. Try different keywords.', 'Нет мест, соответствующих запросу. Попробуйте другие слова.')}
                    </p>
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedCategory('');
                      }}
                      className="px-4 py-2 sm:px-6 sm:py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors touch-manipulation text-sm sm:text-base"
                    >
                      {t('Tüm Konumları Göster', 'Show All Locations', 'Показать все места')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Footer */}
      <Footer />

      {/* QR Scanner Modal */}
      {isQRScannerOpen && (
        <Modal
          isOpen={isQRScannerOpen}
          onClose={() => setIsQRScannerOpen(false)}
        >
          <QRScanner
            isOpen={isQRScannerOpen}
            onScan={handleQRScanResult}
            onClose={() => setIsQRScannerOpen(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default HomePage;
